// web/src/api/tradeCenter/TradeCenterAPI.ts
import {
  CloseOutNoReviewRequestPayload,
  CloseOutTradesRequestPayload,
  DownloadPortfolioStatementRequestPayload,
  DownloadValuationReportRequestPayload,
  GetServerPaginationTradesRequestPayload,
  GetServerPaginationTradesSuccessPayload,
  GetTradesRequestPayload,
  ReviewCloseTradesRequestPayload,
  getKIPDailyValuationDetailsPayload,
} from '@/pages/TradeCenter/store/actions';
import {
  GetDownloadTradeTemplateRequestPayload,
  GetUploadTradeRequestPayload,
} from '@/pages/TradeCenter/store/bulkUploader/action';
import { AxiosInstance, AxiosResponse } from 'axios';

import { ApiResponse } from 'models/ApiResponse';
import { KPIDailyTradesData } from 'pages/TradeCenter/types';

export default class TradeCenterAPI {
  client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async getTrades(payload: GetTradesRequestPayload) {
    return this.client.get('/v2/trade-center/get-trades/', {
      params: payload,
    });
  }

  async downloadPortfolioStatementReport(
    payload: DownloadPortfolioStatementRequestPayload,
  ) {
    return this.client.post(
      '/v2/reporting/portfolio-statement-report/download/',
      payload,
    );
  }

  async downloadValuationReport(
    payload: DownloadValuationReportRequestPayload,
  ) {
    return this.client.post(
      '/v2/reporting/valuation-report/download/',
      payload,
    );
  }

  async bulkCloseTrades(payload: CloseOutTradesRequestPayload) {
    return this.client.post(
      'v2/hedging/transactions/close-transactions-bulk/',
      payload,
    );
  }

  // Update close-out details (or unclose) without the review workflow.
  async closeTradesNoReview(payload: CloseOutNoReviewRequestPayload) {
    return this.client.post(
      'v2/hedging/transactions/close-transactions-bulk-no-review/',
      payload,
    );
  }

  async getPendingClosedTrades() {
    return this.client.post(
      'v2/hedging/transactions/search-for-review-close-transactions/',
    );
  }

  async getClosedTrades() {
    return this.client.post(
      'v2/hedging/transactions/view-closed-transactions/',
    );
  }

  async reviewCloseTrades(payload: ReviewCloseTradesRequestPayload) {
    return this.client.post(
      'v2/hedging/transactions/review-close-transactions-bulk/',
      payload,
    );
  }

  async getUserTrades() {
    return this.client.get('/v2/trade-center/get-my-trades/');
  }

  async downloadTradeTemplate(payload: GetDownloadTradeTemplateRequestPayload) {
    if (payload.company_entity) {
      return this.client.get(
        `/v2/hedging/download-trade-upload-template/?company_entity=${payload.company_entity}`,
      );
    } else if (payload.is_pre_executed) {
      return this.client.get(
        `/v2/hedging/download-trade-upload-template/?is_pre_executed=${payload.is_pre_executed}`,
      );
    } else {
      return this.client.get('/v2/hedging/download-trade-upload-template/');
    }
  }

  async uploadTradeTemplate(payload: GetUploadTradeRequestPayload) {
    return this.client.post(
      '/v2/hedging/upload-trades-from-template/',
      payload,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  }

  async getKPIDailyValuationData(payload: getKIPDailyValuationDetailsPayload) {
    return this.client.post('v2/hedging/trade-stats/', payload);
  }

  async getKPITradesData(): Promise<
    AxiosResponse<ApiResponse<KPIDailyTradesData>>
  > {
    return this.client.post('v2/trade-center/kpis/', {});
  }

  async fetchServerPaginationTrades(
    data: GetServerPaginationTradesRequestPayload,
  ): Promise<AxiosResponse<GetServerPaginationTradesSuccessPayload>> {
    return this.client.post('/v2/trade-center/get-trades-grid/', { ...data });
  }

  async fetchServerPaginationIrTrades(
    data: GetServerPaginationTradesRequestPayload,
  ): Promise<AxiosResponse<GetServerPaginationTradesSuccessPayload>> {
    return this.client.post('/v2/trade-center/get-trades-grid-ir/', data);
  }
}
